<template>
  <div
    v-if="isEnabled"
    class="dev-role-switcher"
    :class="{ 'is-collapsed': isCollapsed, 'is-dragging': isDragging }"
    :style="positionStyle"
  >
    <!-- Collapsed View: Minimal Floating Pill -->
    <div
      v-if="isCollapsed"
      class="switcher-pill"
      @mousedown="startDrag"
      @touchstart="startDrag"
      @click="handlePillClick"
      title="คลิกเพื่อสลับ Role (ลากเพื่อย้ายตำแหน่ง)"
    >
      <span class="drag-handle" title="ลากเพื่อย้าย">⋮⋮</span>
      <span class="pulse-dot"></span>
      <span class="role-icon">{{ activeRoleInfo.icon }}</span>
      <span class="role-title">{{ activeRoleInfo.title }}</span>
      <span v-if="userName" class="user-chip">{{ userName }}</span>
      <span class="chevron">▾</span>
    </div>

    <!-- Expanded View: Full Role Switcher Dock -->
    <div v-else class="switcher-dock">
      <div class="dock-header" @mousedown="startDrag" @touchstart="startDrag">
        <div class="dock-title">
          <span class="drag-handle" title="ลากเพื่อย้าย">⋮⋮</span>
          <span class="pulse-dot"></span>
          <span class="label">สิทธิ์ปัจจุบัน:</span>
          <span class="active-badge" :style="{ backgroundColor: activeRoleInfo.color }">
            {{ activeRoleInfo.icon }} {{ activeRoleInfo.title }}
          </span>
          <span v-if="userName" class="active-username">({{ userName }})</span>
        </div>
        <div class="dock-actions">
          <button
            v-if="pos"
            class="btn-dock-action"
            type="button"
            @click.stop="resetPosition"
            title="รีเซ็ตตำแหน่งกลับมุมล่างขวา"
          >
            ↺
          </button>
          <button
            class="btn-dock-action btn-toggle"
            type="button"
            @click.stop="toggleCollapse(true)"
            title="ย่อขนาด"
          >
            −
          </button>
        </div>
      </div>

      <div class="dock-buttons">
        <button
          v-for="role in roles"
          :key="role.key"
          type="button"
          class="btn-role"
          :class="{
            'active': currentRole === role.key,
            'loading': switchingRole === role.key
          }"
          :disabled="switching"
          @click="selectRole(role.key)"
        >
          <span class="btn-role-icon">{{ role.icon }}</span>
          <span class="btn-role-text">{{ role.title }}</span>
          <span v-if="currentRole === role.key" class="check-mark">✓</span>
          <span v-if="switchingRole === role.key" class="role-spinner"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DevRoleSwitcher',
  data() {
    let savedPos = null;
    try {
      savedPos = JSON.parse(localStorage.getItem('smartfix_dev_switcher_pos'));
    } catch (e) {
      savedPos = null;
    }

    const savedCollapsed = localStorage.getItem('smartfix_dev_switcher_collapsed');

    return {
      // Default collapsed to true to avoid occupying space unless clicked
      isCollapsed: savedCollapsed !== null ? savedCollapsed === 'true' : true,
      pos: savedPos,
      isDragging: false,
      hasMoved: false,
      dragStart: { x: 0, y: 0 },
      initialPos: { x: 0, y: 0 },
      switching: false,
      switchingRole: '',
      roles: [
        { key: 'admin', username: 'admin', title: 'Admin', icon: '👑', color: '#7c3aed', path: '/admin' },
        { key: 'supervisor', username: 'super', title: 'Supervisor', icon: '📋', color: '#0284c7', path: '/supervisor' },
        { key: 'technician', username: 'tech', title: 'ช่างเทคนิค', icon: '🔧', color: '#d97706', path: '/technician' },
        { key: 'user', username: 'user', title: 'ผู้ใช้งาน', icon: '👤', color: '#059669', path: '/user' }
      ]
    };
  },
  computed: {
    isEnabled() {
      return process.env.NODE_ENV === 'development';
    },
    positionStyle() {
      if (this.pos && typeof this.pos.x === 'number' && typeof this.pos.y === 'number') {
        return {
          left: `${this.pos.x}px`,
          top: `${this.pos.y}px`,
          right: 'auto',
          bottom: 'auto'
        };
      }
      return {
        bottom: '20px',
        right: '20px',
        top: 'auto',
        left: 'auto'
      };
    },
    currentRole() {
      const storeRole = this.$store && this.$store.state && this.$store.state.auth ? this.$store.state.auth.role : null;
      if (storeRole) return storeRole;

      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          return JSON.parse(stored).role || 'admin';
        }
      } catch (e) {
        // ignore parse error
      }

      return localStorage.getItem('smartfix_preferred_role') || 'admin';
    },
    userName() {
      const storeProfile = this.$store && this.$store.state && this.$store.state.auth ? this.$store.state.auth.profile : null;
      if (storeProfile && (storeProfile.name || storeProfile.email)) {
        return storeProfile.name || storeProfile.email;
      }
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          const u = JSON.parse(stored);
          return u.name || u.email || '';
        }
      } catch (e) {
        // ignore parse error
      }
      return '';
    },
    activeRoleInfo() {
      const found = this.roles.find(r => r.key === this.currentRole);
      return found || this.roles[0];
    }
  },
  methods: {
    toggleCollapse(val) {
      this.isCollapsed = val;
      try {
        localStorage.setItem('smartfix_dev_switcher_collapsed', String(val));
      } catch (e) {
        // ignore storage error
      }
    },
    handlePillClick() {
      if (this.hasMoved) {
        this.hasMoved = false;
        return;
      }
      this.toggleCollapse(false);
    },
    startDrag(e) {
      if (e.type === 'mousedown' && e.button !== 0) return;
      if (e.target.closest('button') && !e.target.closest('.drag-handle')) return;

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      const el = this.$el;
      const rect = el.getBoundingClientRect();

      this.isDragging = true;
      this.hasMoved = false;
      this.dragStart = { x: clientX, y: clientY };
      this.initialPos = { x: rect.left, y: rect.top };

      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.stopDrag);
      window.addEventListener('touchmove', this.onDrag, { passive: false });
      window.addEventListener('touchend', this.stopDrag);
    },
    onDrag(e) {
      if (!this.isDragging) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      const dx = clientX - this.dragStart.x;
      const dy = clientY - this.dragStart.y;

      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        this.hasMoved = true;
        if (e.cancelable) e.preventDefault();
      }

      if (this.hasMoved) {
        const elWidth = this.$el ? this.$el.offsetWidth : 150;
        const elHeight = this.$el ? this.$el.offsetHeight : 40;
        const maxX = Math.max(8, window.innerWidth - elWidth - 8);
        const maxY = Math.max(8, window.innerHeight - elHeight - 8);

        const newX = Math.max(8, Math.min(this.initialPos.x + dx, maxX));
        const newY = Math.max(8, Math.min(this.initialPos.y + dy, maxY));

        this.pos = { x: newX, y: newY };
      }
    },
    stopDrag() {
      if (this.isDragging) {
        this.isDragging = false;
        window.removeEventListener('mousemove', this.onDrag);
        window.removeEventListener('mouseup', this.stopDrag);
        window.removeEventListener('touchmove', this.onDrag);
        window.removeEventListener('touchend', this.stopDrag);

        if (this.hasMoved && this.pos) {
          try {
            localStorage.setItem('smartfix_dev_switcher_pos', JSON.stringify(this.pos));
          } catch (e) {
            // ignore
          }
        }
      }
    },
    resetPosition() {
      this.pos = null;
      try {
        localStorage.removeItem('smartfix_dev_switcher_pos');
      } catch (e) {
        // ignore
      }
    },
    async selectRole(roleKey) {
      if (this.switching) return;
      if (this.currentRole === roleKey && this.$route && this.$route.path.startsWith(this.activeRoleInfo.path)) {
        return;
      }

      const target = this.roles.find(r => r.key === roleKey);
      if (!target) return;

      this.switching = true;
      this.switchingRole = roleKey;

      try {
        localStorage.setItem('smartfix_preferred_role', roleKey);
        await this.$store.dispatch('auth/testSignIn', {
          username: target.username,
          password: '123456'
        });

        // Ensure user store receives the new profile
        try {
          const userObj = JSON.parse(localStorage.getItem('user'));
          if (userObj) {
            this.$store.commit('user/setUser', userObj);
          }
        } catch (e) {
          // ignore parse error
        }

        // Navigate if not already on matching route
        if (this.$router && this.$route && !this.$route.path.startsWith(target.path)) {
          await this.$router.push(target.path).catch(() => {});
        }
      } catch (err) {
        console.error('[DevRoleSwitcher] Role switch error:', err);
      } finally {
        this.switching = false;
        this.switchingRole = '';
      }
    }
  }
};
</script>

<style scoped>
.dev-role-switcher {
  position: fixed;
  z-index: 99999;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans Thai", sans-serif;
  user-select: none;
  transition: opacity 0.2s ease;
}

.dev-role-switcher.is-dragging {
  cursor: grabbing !important;
  opacity: 0.9;
  transition: none !important;
}

/* Minimal Collapsed Pill */
.switcher-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(15, 23, 42, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  color: #f8fafc;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.switcher-pill:hover {
  background: rgba(30, 41, 59, 0.98);
  border-color: rgba(255, 255, 255, 0.35);
  transform: translateY(-1px);
}

.drag-handle {
  color: #64748b;
  font-size: 13px;
  letter-spacing: -2px;
  cursor: grab;
  padding: 0 2px;
}

.drag-handle:hover {
  color: #94a3b8;
}

.switcher-pill .user-chip {
  background: rgba(255, 255, 255, 0.12);
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  color: #cbd5e1;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switcher-pill .chevron {
  font-size: 11px;
  opacity: 0.6;
}

/* Expanded Dock */
.switcher-dock {
  background: rgba(15, 23, 42, 0.94);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.5);
  border-radius: 14px;
  padding: 10px 14px;
  color: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 320px;
}

.dock-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding-bottom: 6px;
  cursor: grab;
}

.dock-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #94a3b8;
}

.dock-title .label {
  font-weight: 400;
}

.active-badge {
  color: #ffffff;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.active-username {
  color: #cbd5e1;
  font-size: 11px;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dock-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-dock-action {
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 15px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  transition: color 0.15s;
}

.btn-dock-action:hover {
  color: #ffffff;
}

/* Button Grid */
.dock-buttons {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.btn-role {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 6px 8px;
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  position: relative;
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}

.btn-role:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.25);
  color: #ffffff;
  transform: translateY(-1px);
}

.btn-role.active {
  background: rgba(59, 130, 246, 0.22);
  border-color: #3b82f6;
  color: #ffffff;
  font-weight: 600;
  box-shadow: 0 0 12px rgba(59, 130, 246, 0.35);
}

.btn-role:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-role-icon {
  font-size: 15px;
}

.btn-role-text {
  font-size: 11px;
  white-space: nowrap;
}

.check-mark {
  position: absolute;
  top: 3px;
  right: 4px;
  font-size: 9px;
  color: #38bdf8;
  font-weight: bold;
}

/* Pulse Dot */
.pulse-dot {
  width: 8px;
  height: 8px;
  background-color: #10b981;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 8px #10b981;
  animation: pulse-glow 2s infinite;
}

@keyframes pulse-glow {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

/* Spinner */
.role-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-top: 2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
